import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  RefreshCw, Plus, Edit2, Trash2, ChevronDown, ChevronRight,
  Wifi, Server, Activity, AlertTriangle, CheckCircle,
  Info, Settings, Eye, X, Copy, RotateCcw, Zap, FileText
} from 'lucide-react';

import { getTr069DisplayStatus } from './tr069Status';
const API = process.env.REACT_APP_API_URL || '';

const T = {
  bg: '#F0F2F5', surface: '#FFFFFF', surface1: '#F8F9FA',
  primary: '#0052CC', primaryLight: '#E3EDFF', onPrimary: '#FFFFFF',
  onSurface: '#1A1C1E', onSurfaceVar: '#44474F',
  outline: '#C4C6D0', outlineVar: '#E0E2EC',
  error: '#BA1A1A', errorBg: '#FFDAD6',
  success: '#006D3B', successBg: '#E8F5E9',
  warning: '#7D5700', warningBg: '#FFEFD6',
  info: '#0052CC', infoBg: '#E3EDFF',
};

function useApi(token) {
  const request = useCallback(async (path, options = {}) => {
    const response = await fetch(`${API}/api${path}`, {
      ...options,
      headers: { Authorization: `Bearer ${token}`, ...(options.headers || {}) },
    });
    const raw = await response.text();
    let data = {};
    if (raw) {
      try { data = JSON.parse(raw); }
      catch { throw new Error(`Server returned an invalid response (${response.status}). Please refresh and try again.`); }
    }
    if (!response.ok) throw new Error(data.detail || data.message || `Request failed (${response.status})`);
    return data;
  }, [token]);
  const get = useCallback((path) => request(path), [request]);
  const post = useCallback((path, body) => request(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }), [request]);
  const put = useCallback((path, body) => request(path, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }), [request]);
  const del = useCallback((path) => request(path, { method: 'DELETE' }), [request]);
  return { get, post, put, del };
}

function Badge({ color, children }) {
  const colors = {
    green: { bg: T.successBg, text: T.success },
    red: { bg: T.errorBg, text: T.error },
    blue: { bg: T.infoBg, text: T.info },
    gray: { bg: '#F0F2F5', text: '#44474F' },
    orange: { bg: T.warningBg, text: T.warning },
  };
  const c = colors[color] || colors.gray;
  return (
    <span style={{ background: c.bg, color: c.text, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

function CollapsibleSection({ title, defaultOpen = true, children, action }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: `1px solid ${T.outlineVar}`, borderRadius: 8, marginBottom: 16, overflow: 'hidden' }}>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: T.surface1, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(o => !o)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 14, color: T.onSurface }}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          {title}
        </div>
        {action && <div onClick={e => e.stopPropagation()}>{action}</div>}
      </div>
      {open && <div style={{ padding: 16 }}>{children}</div>}
    </div>
  );
}

function Modal({ title, onClose, children, width = 560 }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: T.surface, borderRadius: 12, width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${T.outlineVar}` }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: T.onSurface }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.onSurfaceVar, padding: 4 }}><X size={20} /></button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

function FormField({ label, children, required }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: T.onSurfaceVar, marginBottom: 4 }}>
        {label}{required && <span style={{ color: T.error }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '8px 12px', border: `1px solid ${T.outline}`, borderRadius: 6,
  fontSize: 13, color: T.onSurface, background: T.surface, boxSizing: 'border-box',
};

const btn = (variant = 'primary', size = 'md') => {
  const sizes = { sm: { padding: '4px 10px', fontSize: 12 }, md: { padding: '7px 14px', fontSize: 13 } };
  const variants = {
    primary: { background: T.primary, color: '#fff' },
    success: { background: '#006D3B', color: '#fff' },
    danger: { background: T.error, color: '#fff' },
    outline: { background: 'transparent', color: T.primary, border: `1px solid ${T.primary}` },
    gray: { background: '#E0E2EC', color: T.onSurface },
    blue: { background: '#0052CC', color: '#fff' },
    green: { background: '#006D3B', color: '#fff' },
    red: { background: '#BA1A1A', color: '#fff' },
  };
  return { border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, ...sizes[size], ...variants[variant] };
};

// ── TAB 1: VPN Tunnels ────────────────────────────────────────────────────────
function VPNTunnelsTab({ api }) {
  const [tunnels, setTunnels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editTunnel, setEditTunnel] = useState(null);
  const [mikrotikModal, setMikrotikModal] = useState(null);
  const [form, setForm] = useState({ name: '', tunnel_type: 'openvpn_tcp', subnet: '', connected_subnets: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/noc/vpn-tunnels').then(d => { setTunnels(Array.isArray(d) ? d : []); setLoading(false); }).catch(() => setLoading(false));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm({ name: '', tunnel_type: 'openvpn_tcp', subnet: '', connected_subnets: '' }); setEditTunnel(null); setShowForm(true); };
  const openEdit = (t) => { setForm({ name: t.name, tunnel_type: t.tunnel_type, subnet: t.subnet || '', connected_subnets: t.connected_subnets || '' }); setEditTunnel(t); setShowForm(true); };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editTunnel) await api.put(`/noc/vpn-tunnels/${editTunnel.id}`, form);
      else await api.post('/noc/vpn-tunnels', form);
      setShowForm(false); load();
    } finally { setSaving(false); }
  };

  const deleteTunnel = async (id) => {
    if (!window.confirm('Delete this tunnel?')) return;
    await api.del(`/noc/vpn-tunnels/${id}`); load();
  };

  const openMikrotik = async (t) => {
    setMikrotikModal({ name: t.name, type: t.tunnel_type, loading: true });
    try {
      const data = await api.get(`/noc/vpn-tunnels/${t.id}/mikrotik-setup`);
      setMikrotikModal(data);
    } catch (error) {
      setMikrotikModal({ name: t.name, type: t.tunnel_type, error: error.message || 'Unable to load the MikroTik setup details.' });
    }
  };

  const typeLabel = { openvpn_tcp: 'OpenVPN TCP', openvpn_udp: 'OpenVPN UDP', wireguard: 'WireGuard' };
  const filtered = filterType === 'all' ? tunnels : tunnels.filter(t => t.tunnel_type === filterType);
  const currentType = filterType === 'all' ? (tunnels[0]?.tunnel_type || 'openvpn_tcp') : filterType;

  const fmtBytes = (b) => { if (!b) return '0 B'; if (b > 1048576) return (b / 1048576).toFixed(1) + ' MB'; if (b > 1024) return (b / 1024).toFixed(1) + ' KB'; return b + ' B'; };
  const timeSince = (ts) => { if (!ts) return '—'; const s = Math.floor((Date.now() - new Date(ts)) / 1000); if (s < 60) return `${s}s ago`; if (s < 3600) return `${Math.floor(s / 60)}m ago`; return `${Math.floor(s / 3600)}h ago`; };

  return (
    <div>
      <CollapsibleSection title="Info" defaultOpen={false}>
        <p style={{ margin: 0, fontSize: 13, color: T.onSurfaceVar, lineHeight: 1.6 }}>
          VPN Tunnels allow secure connectivity between remote sites and the NOC. Supports OpenVPN TCP, OpenVPN UDP, and WireGuard protocols. Use the Mikrotik VPN Setup button to generate router configuration scripts.
        </p>
      </CollapsibleSection>

      <CollapsibleSection title="Tunnel status" defaultOpen={true} action={<button onClick={load} style={btn('outline', 'sm')}><RefreshCw size={13} /> Refresh</button>}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {['all', 'openvpn_tcp', 'openvpn_udp', 'wireguard'].map(t => (
              <button key={t} onClick={() => setFilterType(t)} style={btn(filterType === t ? 'primary' : 'gray', 'sm')}>
                {t === 'all' ? 'All' : typeLabel[t]}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 12, color: T.onSurfaceVar }}>Current: <strong>{typeLabel[currentType]}</strong></span>
        </div>

        {loading ? <div style={{ textAlign: 'center', padding: 32, color: T.onSurfaceVar }}>Loading...</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.surface1 }}>
                  {['#', 'User/Tunnel Name', 'Status', 'Subnet', 'Connected Subnets', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: T.onSurfaceVar, borderBottom: `1px solid ${T.outlineVar}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: T.onSurfaceVar }}>No tunnels found</td></tr>
                ) : filtered.map((t, i) => (
                  <tr key={t.id} style={{ borderBottom: `1px solid ${T.outlineVar}` }}>
                    <td style={{ padding: '8px 12px', color: T.onSurfaceVar }}>{i + 1}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <strong>{t.name}</strong>
                        <Info size={13} style={{ color: T.onSurfaceVar }} title={`Type: ${typeLabel[t.tunnel_type]}`} />
                      </div>
                      <div style={{ fontSize: 11, color: T.onSurfaceVar }}>{typeLabel[t.tunnel_type]}</div>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {t.status === 'connected' ? (
                        <div>
                          <Badge color="green">Connected</Badge>
                          <div style={{ fontSize: 11, color: T.onSurfaceVar, marginTop: 3 }}>
                            {t.connected_from && <span>IP: {t.connected_from}</span>}
                            {t.tunnel_ip && <span> · Tunnel: {t.tunnel_ip}</span>}
                          </div>
                          <div style={{ fontSize: 11, color: T.onSurfaceVar }}>
                            ↑ {fmtBytes(t.bytes_sent)} / ↓ {fmtBytes(t.bytes_received)}
                            {t.connected_since && <span> · Since: {timeSince(t.connected_since)}</span>}
                          </div>
                        </div>
                      ) : <Badge color="gray">Disconnected</Badge>}
                    </td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>{t.subnet || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      {t.connected_subnets ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {t.connected_subnets.split(',').map((s, i) => (
                            <span key={i} style={{ fontFamily: 'monospace', fontSize: 11, background: T.surface1, padding: '1px 6px', borderRadius: 4 }}>{s.trim()}</span>
                          ))}
                        </div>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        <button onClick={() => openMikrotik(t)} style={btn('blue', 'sm')}><Settings size={12} /> Mikrotik</button>
                        <button onClick={() => openEdit(t)} style={btn('green', 'sm')}><Edit2 size={12} /> Edit</button>
                        <button onClick={() => deleteTunnel(t.id)} style={btn('red', 'sm')}><Trash2 size={12} /> Del</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ marginTop: 16 }}>
          <button onClick={openCreate} style={btn('success', 'md')}><Plus size={15} /> Create a new tunnel</button>
        </div>
      </CollapsibleSection>

      {showForm && (
        <Modal title={editTunnel ? 'Edit VPN Tunnel' : 'Create VPN Tunnel'} onClose={() => setShowForm(false)}>
          <FormField label="Tunnel Name" required>
            <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. HQ-VPN-01" />
          </FormField>
          <FormField label="Tunnel Type">
            <select style={inputStyle} value={form.tunnel_type} onChange={e => setForm(f => ({ ...f, tunnel_type: e.target.value }))}>
              <option value="openvpn_tcp">OpenVPN TCP</option>
              <option value="openvpn_udp">OpenVPN UDP</option>
              <option value="wireguard">WireGuard</option>
            </select>
          </FormField>
          <FormField label="Subnet (CIDR)">
            <input style={inputStyle} value={form.subnet} onChange={e => setForm(f => ({ ...f, subnet: e.target.value }))} placeholder="e.g. 10.8.0.0/24" />
          </FormField>
          <FormField label="Connected Subnets (comma-separated)">
            <input style={inputStyle} value={form.connected_subnets} onChange={e => setForm(f => ({ ...f, connected_subnets: e.target.value }))} placeholder="e.g. 192.168.1.0/24,10.10.0.0/16" />
          </FormField>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <button onClick={() => setShowForm(false)} style={btn('gray')}>Cancel</button>
            <button onClick={save} disabled={saving} style={btn('success')}>{saving ? 'Saving...' : (editTunnel ? 'Update' : 'Create')}</button>
          </div>
        </Modal>
      )}

      {mikrotikModal && (
        <Modal title={`Mikrotik VPN Setup — ${mikrotikModal.name}`} onClose={() => setMikrotikModal(null)} width={640}>
          <div style={{ marginBottom: 12 }}><Badge color="blue">{(mikrotikModal.type || '').toUpperCase()}</Badge></div>
          {mikrotikModal.loading ? <div style={{ color: T.onSurfaceVar, padding: 16 }}>Loading secure setup details...</div>
            : mikrotikModal.error ? <div role="alert" style={{ background: T.errorBg, color: T.error, padding: 12, borderRadius: 6, fontSize: 13 }}>{mikrotikModal.error}</div>
              : <>
                <pre style={{ background: '#1E1E2E', color: '#CDD6F4', padding: 16, borderRadius: 8, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{mikrotikModal.config || 'No setup commands are available for this tunnel.'}</pre>
                <button onClick={() => navigator.clipboard?.writeText(mikrotikModal.config || '')} style={{ ...btn('outline', 'sm'), marginTop: 10 }}><Copy size={13} /> Copy Config</button>
              </>}
        </Modal>
      )}
    </div>
  );
}

// ── TAB 2: TR069 Profiles ─────────────────────────────────────────────────────
function TR069ProfilesTab({ api }) {
  const [profiles, setProfiles] = useState([]);
  const [olts, setOlts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editProfile, setEditProfile] = useState(null);
  const [oltModal, setOltModal] = useState(null);
  const [filesModal, setFilesModal] = useState(null);
  const [selectedOlts, setSelectedOlts] = useState([]);
  const [form, setForm] = useState({ name: '', cwmp_acs_url: '', nbi_url: '', fs_url: '', acs_type: 'integrated', username: '', password: '', status: 'active' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get('/noc/tr069-profiles'), api.get('/noc/olts').catch(() => [])]).then(([p, o]) => {
      setProfiles(Array.isArray(p) ? p : []); setOlts(Array.isArray(o) ? o : []); setLoading(false);
    }).catch(() => setLoading(false));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm({ name: '', cwmp_acs_url: '', nbi_url: '', fs_url: '', acs_type: 'integrated', username: '', password: '', status: 'active' }); setEditProfile(null); setShowForm(true); };
  const openEdit = (p) => { setForm({ name: p.name, cwmp_acs_url: p.cwmp_acs_url || '', nbi_url: p.nbi_url || '', fs_url: p.fs_url || '', acs_type: p.acs_type || 'integrated', username: p.username || '', password: '', status: p.status || 'active' }); setEditProfile(p); setShowForm(true); };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editProfile) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/noc/tr069-profiles/${editProfile.id}`, payload);
      } else await api.post('/noc/tr069-profiles', form);
      setShowForm(false); load();
    } finally { setSaving(false); }
  };

  const deleteProfile = async (id) => {
    if (!window.confirm('Delete this profile?')) return;
    await api.del(`/noc/tr069-profiles/${id}`); load();
  };

  const openOltModal = (p) => { setOltModal(p); setSelectedOlts((p.olts || []).map(o => o.id)); };
  const saveOlts = async () => { await api.post(`/noc/tr069-profiles/${oltModal.id}/set-olts`, { olt_ids: selectedOlts }); setOltModal(null); load(); };
  const openFiles = async (p) => {
    setFilesModal({ profile: p, loading: true });
    try {
      const data = await api.get(`/noc/tr069-profiles/${p.id}/files`);
      setFilesModal({ profile: p, files: Array.isArray(data.files) ? data.files : [], message: data.message || '' });
    } catch (error) {
      setFilesModal({ profile: p, error: error.message || 'Unable to load ACS file details.' });
    }
  };

  return (
    <div>
      <CollapsibleSection title="Info" defaultOpen={false}>
        <p style={{ margin: 0, fontSize: 13, color: T.onSurfaceVar, lineHeight: 1.6 }}>
          TR-069 profiles define ACS (Auto Configuration Server) connections for ONU device management. Each profile can be linked to multiple OLTs. Supports GenieACS and other CWMP-compliant ACS systems.
        </p>
      </CollapsibleSection>

      <CollapsibleSection title="Defined profiles" defaultOpen={true} action={<button onClick={load} style={btn('outline', 'sm')}><RefreshCw size={13} /> Refresh</button>}>
        {loading ? <div style={{ textAlign: 'center', padding: 32, color: T.onSurfaceVar }}>Loading...</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.surface1 }}>
                  {['Profile Name', 'CWMP ACS', 'Status', 'OLTs', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: T.onSurfaceVar, borderBottom: `1px solid ${T.outlineVar}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {profiles.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: T.onSurfaceVar }}>No profiles found</td></tr>
                ) : profiles.map(p => (
                  <tr key={p.id} style={{ borderBottom: `1px solid ${T.outlineVar}` }}>
                    <td style={{ padding: '8px 12px' }}><strong style={{ color: T.primary }}>{p.name}</strong></td>
                    <td style={{ padding: '8px 12px' }}>
                      {p.cwmp_acs_url ? <a href={p.cwmp_acs_url} target="_blank" rel="noreferrer" style={{ color: T.primary, fontSize: 12, fontFamily: 'monospace' }}>{p.cwmp_acs_url}</a> : '—'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>{p.cwmp_acs_url ? <Badge color="green">CWMP 🟢</Badge> : <span style={{ color: T.onSurfaceVar }}>—</span>}</td>
                    <td style={{ padding: '8px 12px' }}><span style={{ fontSize: 12, color: T.onSurfaceVar }}>{p.olts && p.olts.length > 0 ? `All selected (${p.olts.length})` : 'None'}</span></td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        <button onClick={() => openOltModal(p)} style={btn('green', 'sm')}><Settings size={12} /> Set OLTs</button>
                        <button onClick={() => openEdit(p)} style={btn('blue', 'sm')}><Eye size={12} /> View</button>
                        <button onClick={() => openFiles(p)} style={btn('gray', 'sm')}><FileText size={12} /> Files</button>
                        <button onClick={() => deleteProfile(p.id)} style={btn('red', 'sm')}><Trash2 size={12} /> Del</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ marginTop: 16 }}><button onClick={openCreate} style={btn('success', 'md')}><Plus size={15} /> Add a new profile</button></div>
      </CollapsibleSection>

      {showForm && (
        <Modal title={editProfile ? 'Edit TR-069 Profile' : 'Add TR-069 Profile'} onClose={() => setShowForm(false)} width={600}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ gridColumn: '1/-1' }}>
              <FormField label="Profile Name" required>
                <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. GenieACS-Main" />
              </FormField>
            </div>
            <FormField label="CWMP ACS URL"><input style={inputStyle} value={form.cwmp_acs_url} onChange={e => setForm(f => ({ ...f, cwmp_acs_url: e.target.value }))} placeholder="http://10.69.69.1:14501" /></FormField>
            <FormField label="NBI URL"><input style={inputStyle} value={form.nbi_url} onChange={e => setForm(f => ({ ...f, nbi_url: e.target.value }))} placeholder="http://10.69.69.1:7557" /></FormField>
            <FormField label="File Server URL"><input style={inputStyle} value={form.fs_url} onChange={e => setForm(f => ({ ...f, fs_url: e.target.value }))} placeholder="http://10.69.69.1:7567" /></FormField>
            <FormField label="ACS Type">
              <select style={inputStyle} value={form.acs_type} onChange={e => setForm(f => ({ ...f, acs_type: e.target.value }))}>
                <option value="integrated">Integrated</option>
                <option value="external">External</option>
              </select>
            </FormField>
            <FormField label="Username"><input style={inputStyle} value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} placeholder="admin" /></FormField>
            <FormField label={editProfile ? 'Password (blank = keep)' : 'Password'}>
              <input style={inputStyle} type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="••••••••" />
            </FormField>
            <FormField label="Status">
              <select style={inputStyle} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <button onClick={() => setShowForm(false)} style={btn('gray')}>Cancel</button>
            <button onClick={save} disabled={saving} style={btn('success')}>{saving ? 'Saving...' : (editProfile ? 'Update' : 'Add Profile')}</button>
          </div>
        </Modal>
      )}

      {oltModal && (
        <Modal title={`Set OLTs — ${oltModal.name}`} onClose={() => setOltModal(null)}>
          <p style={{ fontSize: 13, color: T.onSurfaceVar, marginTop: 0 }}>Select OLTs to associate with this TR-069 profile:</p>
          <div style={{ maxHeight: 300, overflowY: 'auto', border: `1px solid ${T.outlineVar}`, borderRadius: 6, padding: 8 }}>
            {olts.length === 0 ? <div style={{ color: T.onSurfaceVar, fontSize: 13, padding: 8 }}>No OLTs available</div> : olts.map(o => (
              <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={selectedOlts.includes(o.id)} onChange={e => setSelectedOlts(prev => e.target.checked ? [...prev, o.id] : prev.filter(id => id !== o.id))} />
                {o.name} {o.ip_address && <span style={{ color: T.onSurfaceVar, fontSize: 11 }}>({o.ip_address})</span>}
              </label>
            ))}
          </div>
          <div style={{ marginTop: 8, fontSize: 12, color: T.onSurfaceVar }}>{selectedOlts.length} OLT(s) selected</div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <button onClick={() => setOltModal(null)} style={btn('gray')}>Cancel</button>
            <button onClick={saveOlts} style={btn('success')}>Save OLT Assignment</button>
          </div>
        </Modal>
      )}

      {filesModal && (
        <Modal title={`ACS Files — ${filesModal.profile.name}`} onClose={() => setFilesModal(null)} width={600}>
          {filesModal.loading ? <div style={{ color: T.onSurfaceVar, padding: 12 }}>Loading ACS file details...</div>
            : filesModal.error ? <div role="alert" style={{ background: T.errorBg, color: T.error, padding: 12, borderRadius: 6, fontSize: 13 }}>{filesModal.error}</div>
              : filesModal.files.length === 0 ? <div style={{ color: T.onSurfaceVar, fontSize: 13, lineHeight: 1.6 }}>{filesModal.message || 'No ACS files are registered for this profile.'}</div>
                : <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}><tbody>{filesModal.files.map((file, index) => <tr key={file.name || index}><td style={{ padding: 8, borderBottom: `1px solid ${T.outlineVar}` }}>{file.name || String(file)}</td></tr>)}</tbody></table>}
        </Modal>
      )}
    </div>
  );
}

// ── TAB 3: TR069 Status ───────────────────────────────────────────────────────
function TR069StatusTab({ api }) {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState([]);
  const [faults, setFaults] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [paramsModal, setParamsModal] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, f, d] = await Promise.all([api.get('/noc/tr069-status'), api.get('/noc/tr069-faults'), api.get('/noc/tr069-devices')]);
      setStats(s); setFaults(Array.isArray(f) ? f : []); setDevices(Array.isArray(d) ? d : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  const refreshHealth = useCallback(async () => {
    setHealthLoading(true);
    try { const h = await api.get('/noc/tr069-health'); setHealth(Array.isArray(h) ? h : []); } catch (e) { console.error(e); }
    setHealthLoading(false);
  }, [api]);

  useEffect(() => {
    load(); refreshHealth();
    timerRef.current = setInterval(() => load(), 30000);
    return () => clearInterval(timerRef.current);
  }, [load, refreshHealth]);

  const resolveFault = async (id) => {
    await api.post(`/noc/tr069-faults/${id}/resolve`, {});
    setFaults(prev => prev.filter(f => f.id !== id));
    setStats(prev => prev ? { ...prev, active_faults: Math.max(0, (prev.active_faults || 1) - 1) } : prev);
  };

  const viewParams = async (d) => {
    setParamsModal({ device: d, loading: true, params: {} });
    try {
      const data = await api.get(`/noc/tr069-devices/${d.id}/parameters`);
      setParamsModal({ device: d, params: data.parameters || {} });
    } catch (error) {
      setParamsModal({ device: d, params: {}, error: error.message || 'Unable to load device parameters.' });
    }
  };

  const reboot = async (d) => {
    if (!window.confirm(`Reboot ${d.serial || d.device_id}?`)) return;
    try { const result = await api.post(`/noc/tr069-devices/${d.id}/reboot`, {}); alert(result.message || 'Reboot task accepted by GenieACS.'); }
    catch (error) { alert(error.message || 'GenieACS did not accept the reboot request.'); }
  };
  const factoryReset = async (d) => {
    if (!window.confirm(`Factory reset ${d.serial || d.device_id}? Cannot be undone!`)) return;
    try { const result = await api.post(`/noc/tr069-devices/${d.id}/factory-reset`, {}); alert(result.message || 'Factory reset task accepted by GenieACS.'); }
    catch (error) { alert(error.message || 'GenieACS did not accept the factory reset request.'); }
  };

  const filteredDevices = devices.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [(d.device_id || ''), (d.serial || ''), (d.model || ''), (d.description || ''), (d.ip_address || '')].some(v => v.toLowerCase().includes(q));
  });

  const fmtTime = (ts) => ts ? new Date(ts).toLocaleString() : '—';
  const SvcBadge = ({ ok, label }) => {
    const configured = ok !== null && ok !== undefined;
    const color = !configured ? T.outline : ok ? T.success : T.error;
    const icon = !configured ? '⚪' : ok ? '🟢' : '🔴';
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 600, color }}>{icon} {label}{!configured ? ' · Not configured' : ''}</span>;
  };

  return (
    <div>
      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'TR069-managed ONUs', value: stats.total, color: T.onSurfaceVar, bg: T.surface1 },
            { label: 'Online (informed < 60 min)', value: stats.online, color: T.info, bg: T.infoBg },
            { label: 'Not informed recently', value: stats.not_informed, color: T.onSurfaceVar, bg: T.surface1 },
            { label: 'Active faults', value: stats.active_faults, color: T.error, bg: T.errorBg },
          ].map((c, i) => (
            <div key={i} style={{ background: c.bg, borderRadius: 10, padding: '14px 18px', border: `1px solid ${T.outlineVar}` }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: c.color }}>{c.value ?? '—'}</div>
              <div style={{ fontSize: 12, color: T.onSurfaceVar, marginTop: 2 }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ACS Health */}
      <div style={{ border: `1px solid ${T.outlineVar}`, borderRadius: 8, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: T.surface1 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: T.onSurface, display: 'flex', alignItems: 'center', gap: 8 }}><Activity size={16} /> ACS / GenieACS health</div>
          <button onClick={refreshHealth} disabled={healthLoading} style={btn('outline', 'sm')}><RefreshCw size={13} /> Refresh</button>
        </div>
        <div style={{ padding: 16 }}>
          {health.length === 0 ? (
            <div style={{ color: T.onSurfaceVar, fontSize: 13, textAlign: 'center', padding: 16 }}>{healthLoading ? 'Checking ACS health...' : 'No active profiles'}</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: T.surface1 }}>
                    {['Profile', 'Type', 'NBI Endpoint', 'Services', 'Devices in ACS', 'Faults'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: T.onSurfaceVar, borderBottom: `1px solid ${T.outlineVar}`, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {health.map(h => (
                    <tr key={h.profile_id} style={{ borderBottom: `1px solid ${T.outlineVar}` }}>
                      <td style={{ padding: '8px 12px', fontWeight: 600 }}>{h.profile_name}</td>
                      <td style={{ padding: '8px 12px' }}><Badge color="blue">{h.acs_type}</Badge></td>
                      <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 11 }}>{h.nbi_url || '—'}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <SvcBadge ok={h.cwmp_ok} label="CWMP" />
                          <SvcBadge ok={h.nbi_ok} label="NBI" />
                          <SvcBadge ok={h.fs_ok} label="FS" />
                        </div>
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>{h.device_count}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>{h.fault_count > 0 ? <Badge color="red">{h.fault_count}</Badge> : <Badge color="green">0</Badge>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input style={{ ...inputStyle, maxWidth: 500 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search ONUs (serial, IP, description, OLT, model) and faults..." />
      </div>

      {/* Active Faults */}
      <div style={{ border: `1px solid ${T.outlineVar}`, borderRadius: 8, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ padding: '10px 16px', background: T.surface1, fontWeight: 600, fontSize: 14, color: T.onSurface, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={16} style={{ color: T.error }} />
          Active faults &amp; provisioning errors
          <span style={{ background: T.errorBg, color: T.error, padding: '1px 8px', borderRadius: 12, fontSize: 12, fontWeight: 700 }}>{faults.length}</span>
        </div>
        <div style={{ padding: 16 }}>
          {faults.length === 0 ? (
            <div style={{ color: T.success, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}><CheckCircle size={16} /> No active faults</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: T.surface1 }}>
                    {['When', 'Profile', 'Device', 'Channel', 'Code', 'Message', 'Retries', 'Action'].map(h => (
                      <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: T.onSurfaceVar, borderBottom: `1px solid ${T.outlineVar}`, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {faults.filter(f => !search || (f.device_id || '').toLowerCase().includes(search.toLowerCase()) || (f.fault_message || '').toLowerCase().includes(search.toLowerCase())).map(f => (
                    <tr key={f.id} style={{ borderBottom: `1px solid ${T.outlineVar}` }}>
                      <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>{fmtTime(f.created_at)}</td>
                      <td style={{ padding: '7px 10px' }}>{f.profile_name || '—'}</td>
                      <td style={{ padding: '7px 10px', fontFamily: 'monospace', color: T.primary }}>{f.device_id || '—'}</td>
                      <td style={{ padding: '7px 10px' }}>{f.channel || '—'}</td>
                      <td style={{ padding: '7px 10px' }}><Badge color="red">{f.fault_code || '—'}</Badge></td>
                      <td style={{ padding: '7px 10px', maxWidth: 200 }}>{f.fault_message || '—'}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'center' }}>{f.retries || 0}</td>
                      <td style={{ padding: '7px 10px' }}><button onClick={() => resolveFault(f.id)} style={btn('green', 'sm')}><CheckCircle size={11} /> Resolve</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ONU Inventory */}
      <div style={{ border: `1px solid ${T.outlineVar}`, borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '10px 16px', background: T.surface1, fontWeight: 600, fontSize: 14, color: T.onSurface, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Server size={16} /> TR069 ONU inventory
          <span style={{ background: T.infoBg, color: T.info, padding: '1px 8px', borderRadius: 12, fontSize: 12, fontWeight: 700 }}>{filteredDevices.length}</span>
        </div>
        <div style={{ padding: 16 }}>
          {loading ? <div style={{ textAlign: 'center', padding: 32, color: T.onSurfaceVar }}>Loading devices...</div> : filteredDevices.length === 0 ? (
            <div style={{ color: T.onSurfaceVar, fontSize: 13, textAlign: 'center', padding: 24 }}>No TR-069 managed devices found</div>
          ) : (
            <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead style={{ position: 'sticky', top: 0, background: T.surface1, zIndex: 1 }}>
                  <tr>
                    {['Device ID', 'Serial', 'OLT', 'Model', 'Description', 'IP', 'Last Inform', 'State', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: T.onSurfaceVar, borderBottom: `1px solid ${T.outlineVar}`, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredDevices.map(d => (
                    <tr key={d.id} style={{ borderBottom: `1px solid ${T.outlineVar}` }}>
                      <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontSize: 11, color: T.primary }}>{d.device_id || '—'}</td>
                      <td style={{ padding: '7px 10px', fontFamily: 'monospace' }}>{d.serial || '—'}</td>
                      <td style={{ padding: '7px 10px' }}>{d.olt_id || '—'}</td>
                      <td style={{ padding: '7px 10px' }}>{d.model || '—'}</td>
                      <td style={{ padding: '7px 10px', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.description || '—'}</td>
                      <td style={{ padding: '7px 10px', fontFamily: 'monospace' }}>{d.ip_address || '—'}</td>
                      <td style={{ padding: '7px 10px', whiteSpace: 'nowrap' }}>{fmtTime(d.last_inform)}</td>
                      <td style={{ padding: '7px 10px' }}>{(() => { const status = getTr069DisplayStatus(d); return <Badge color={status === 'online' ? 'green' : status === 'fault' ? 'red' : 'gray'}>{status}</Badge>; })()}</td>
                      <td style={{ padding: '7px 10px' }}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => viewParams(d)} style={btn('blue', 'sm')} title="Parameters"><Eye size={11} /></button>
                          <button onClick={() => reboot(d)} style={btn('gray', 'sm')} title="Reboot"><RotateCcw size={11} /></button>
                          <button onClick={() => factoryReset(d)} style={btn('red', 'sm')} title="Factory Reset"><Zap size={11} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {paramsModal && (
        <Modal title={`Device Parameters — ${paramsModal.device.serial || paramsModal.device.device_id}`} onClose={() => setParamsModal(null)} width={600}>
          {paramsModal.loading ? <div style={{ color: T.onSurfaceVar, padding: 12 }}>Loading device parameters...</div>
            : paramsModal.error ? <div role="alert" style={{ background: T.errorBg, color: T.error, padding: 12, borderRadius: 6, fontSize: 13 }}>{paramsModal.error}</div>
              : <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: T.surface1 }}>
                  <th style={{ padding: '7px 12px', textAlign: 'left', fontWeight: 600, color: T.onSurfaceVar, borderBottom: `1px solid ${T.outlineVar}` }}>Parameter</th>
                  <th style={{ padding: '7px 12px', textAlign: 'left', fontWeight: 600, color: T.onSurfaceVar, borderBottom: `1px solid ${T.outlineVar}` }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(paramsModal.params).map(([k, v]) => (
                  <tr key={k} style={{ borderBottom: `1px solid ${T.outlineVar}` }}>
                    <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: 11, color: T.onSurfaceVar }}>{k}</td>
                    <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: 11 }}>{String(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
        </Modal>
      )}
    </div>
  );
}

function TestHg8546mWifiTab({ api }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api.get('/noc/tr069/wifi-test-hg8546m');
      setStatus(data); setError('');
    } catch (e) { setError(e.message || 'Unable to load Wi-Fi status.'); }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  const fmtTime = (ts) => ts ? new Date(ts).toLocaleString() : '—';
  const submitPassword = async (event) => {
    event.preventDefault();
    setSaving(true); setResult('');
    try {
      const response = await api.post('/noc/tr069/wifi-test-hg8546m/password', { password, confirmation });
      setResult(response.state === 'queued' ? 'Password task queued for the next CPE Inform.' : 'Password task submitted.');
      setPassword(''); setConfirmation(''); setPasswordOpen(false); load();
    } catch (e) { setResult(e.message || 'Unable to queue password task.'); }
    setSaving(false);
  };

  const clients = status?.clients || [];
  const wifi = status?.wifi || {};
  const cwmp = status?.cwmp || {};
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Wifi size={20} color={T.primary} /><h2 style={{ margin: 0, fontSize: 18, color: T.onSurface }}>CPE Wi-Fi Management</h2><Badge color="orange">Test HG8546M only</Badge></div>
          <p style={{ margin: '5px 0 0', color: T.onSurfaceVar, fontSize: 13 }}>Live GenieACS data for validated router DC91E99A. Refreshes automatically every 30 seconds.</p>
        </div>
        <button onClick={load} style={btn('outline', 'sm')} disabled={loading}><RefreshCw size={13} /> Refresh</button>
      </div>

      {error && <div role="alert" style={{ padding: 12, background: T.errorBg, color: T.error, borderRadius: 8, marginBottom: 16 }}>{error}</div>}
      {loading && !status ? <div style={{ padding: 28, color: T.onSurfaceVar, textAlign: 'center' }}>Loading verified CPE Wi-Fi status...</div> : status && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
          {[
            { label: 'SSID', value: wifi.ssid || '—', bg: T.primaryLight, color: T.primary },
            { label: 'WLAN state', value: wifi.status || '—', bg: wifi.status === 'Up' ? T.successBg : T.errorBg, color: wifi.status === 'Up' ? T.success : T.error },
            { label: 'Connected clients', value: wifi.clientCount ?? '—', bg: T.infoBg, color: T.info },
            { label: 'CWMP last Inform', value: fmtTime(status.device?.lastInform), bg: T.surface1, color: T.onSurface },
          ].map((item) => <div key={item.label} style={{ background: item.bg, padding: 14, borderRadius: 10, border: `1px solid ${T.outlineVar}` }}><div style={{ color: item.color, fontSize: item.label === 'CWMP last Inform' ? 13 : 22, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.value}</div><div style={{ marginTop: 4, fontSize: 12, color: T.onSurfaceVar }}>{item.label}</div></div>)}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <Badge color={cwmp.faultCount > 0 ? 'red' : 'green'}>{cwmp.faultCount || 0} ACS faults</Badge>
          <Badge color={cwmp.pendingTaskCount > 0 ? 'orange' : 'green'}>{cwmp.pendingTaskCount || 0} pending tasks</Badge>
          <Badge color="blue">{status.device?.serial} · {status.device?.model}</Badge>
        </div>

        <CollapsibleSection title={`Associated Wi-Fi clients (${wifi.clientCount ?? clients.length})`} defaultOpen>
          {clients.length === 0 ? <div style={{ color: T.onSurfaceVar, fontSize: 13 }}>No associated Wi-Fi client currently reported by the CPE.</div> : <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}><thead><tr style={{ background: T.surface1 }}>{['Client', 'IP address', 'Auth', 'Signal', 'Rate', 'Uptime'].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: T.onSurfaceVar, borderBottom: `1px solid ${T.outlineVar}` }}>{h}</th>)}</tr></thead><tbody>{clients.map(client => <tr key={client.instance} style={{ borderBottom: `1px solid ${T.outlineVar}` }}><td style={{ padding: '8px 10px', fontWeight: 600 }}>{client.name}<div style={{ color: T.onSurfaceVar, fontFamily: 'monospace', fontSize: 10 }}>{client.macSuffix || 'MAC hidden'}</div></td><td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{client.ipAddress || '—'}</td><td style={{ padding: '8px 10px' }}><Badge color={client.authenticated ? 'green' : 'gray'}>{client.authenticated ? 'Authenticated' : 'Pending'}</Badge></td><td style={{ padding: '8px 10px' }}>{client.rssi != null ? `${client.rssi} dBm · SNR ${client.snr ?? '—'}` : '—'}</td><td style={{ padding: '8px 10px' }}>{client.rxRate || '—'} / {client.txRate || '—'} Mbps</td><td style={{ padding: '8px 10px' }}>{client.uptime ? `${client.uptime}s` : '—'}</td></tr>)}</tbody></table></div>}
        </CollapsibleSection>

        <CollapsibleSection title="Wi-Fi password" defaultOpen={false} action={<button onClick={() => { setResult(''); setPasswordOpen(true); }} style={btn('blue', 'sm')}><Settings size={12} /> Change password</button>}>
          <div style={{ color: T.onSurfaceVar, fontSize: 13, lineHeight: 1.55 }}>The CPE does not expose its current Wi-Fi password back to the ACS. A change is queued for the next CWMP Inform and cannot be automatically rolled back to the prior secret. This control is locked to the validated test HG8546M only.</div>
          {result && <div role="status" style={{ marginTop: 10, padding: 10, borderRadius: 7, background: result.includes('queued') ? T.successBg : T.errorBg, color: result.includes('queued') ? T.success : T.error, fontSize: 13 }}>{result}</div>}
        </CollapsibleSection>
      </>}

      {passwordOpen && <Modal title="Change test HG8546M Wi-Fi password" onClose={() => { if (!saving) setPasswordOpen(false); }} width={520}>
        <form onSubmit={submitPassword} style={{ display: 'grid', gap: 14 }}>
          <div style={{ background: T.warningBg, color: T.warning, padding: 12, borderRadius: 8, fontSize: 13, lineHeight: 1.45 }}><strong>Test device only.</strong> The prior password is redacted by the CPE and cannot be restored automatically. The new task will be delivered at the next periodic Inform.</div>
          <FormField label="New Wi-Fi password" required><input type="password" autoComplete="new-password" style={inputStyle} value={password} onChange={e => setPassword(e.target.value)} minLength={12} maxLength={63} required placeholder="12–63 characters; uppercase, lowercase, number, symbol" /></FormField>
          <FormField label='Type “CHANGE TEST WIFI PASSWORD” to confirm' required><input style={inputStyle} value={confirmation} onChange={e => setConfirmation(e.target.value)} required /></FormField>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button type="button" style={btn('outline', 'sm')} disabled={saving} onClick={() => setPasswordOpen(false)}>Cancel</button><button type="submit" style={btn('blue', 'sm')} disabled={saving || confirmation !== 'CHANGE TEST WIFI PASSWORD'}><Wifi size={13} /> {saving ? 'Queuing…' : 'Queue password change'}</button></div>
        </form>
      </Modal>}
    </div>
  );
}

// ── Main TR069Page ─────────────────────────────────────────────────────────────
export default function TR069Page() {
  const token = localStorage.getItem('session_token') || localStorage.getItem('token') || '';
  const api = useApi(token);
  const [activeTab, setActiveTab] = useState('vpn');

  const tabs = [
    { id: 'vpn', label: 'VPN Tunnels', icon: <Wifi size={15} /> },
    { id: 'profiles', label: 'TR069 Profiles', icon: <Server size={15} /> },
    { id: 'status', label: 'TR069 Status', icon: <Activity size={15} /> },
  ];

  return (
    <div style={{ background: T.bg, minHeight: '100vh', padding: 20 }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: T.onSurface }}>TR-069 / ACS Management</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: T.onSurfaceVar }}>Manage VPN tunnels, TR-069 profiles, and monitor ACS device status</p>
        </div>

        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: T.surface, borderRadius: 10, padding: 6, border: `1px solid ${T.outlineVar}`, width: 'fit-content' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ ...btn(activeTab === t.id ? 'primary' : 'gray', 'md'), borderRadius: 7, transition: 'all 0.15s' }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div style={{ background: T.surface, borderRadius: 12, padding: 20, border: `1px solid ${T.outlineVar}` }}>
          {activeTab === 'vpn' && <VPNTunnelsTab api={api} />}
          {activeTab === 'profiles' && <TR069ProfilesTab api={api} />}
          {activeTab === 'status' && <TR069StatusTab api={api} />}
        </div>
      </div>
    </div>
  );
}
